import { LocalStorageReactive } from '@adornis/base/rxjs-extension/index.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { getMenteeUserClass, getMentorUserClass } from '@adornis/bh-matching-matches/user-class.js';
import { currentlySelectedTimeframe } from '@adornis/bh-matching-timeframes/components/matching-semester-selection.js';
import type { TranslationDictionaryBuddy } from '@adornis/bh-matching-translation/translations.js';
import { BuddyUser, BuddyUserMentee, BuddyUserMentor } from '@adornis/bh-matching-user/entities/buddy-user.js';
import '@adornis/buchungsheld-theme/components/tr-checkbox.js';
import '@adornis/buchungsheld-theme/components/tr-data-view.js';
import '@adornis/buchungsheld-theme/components/tr-input.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex';
import { LOADER } from '@adornis/chemistry/elements/components/x-loader.js';
import '@adornis/chemistry/elements/components/x-section';
import '@adornis/chemistry/elements/components/x-tabs';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { StringAspectConfig } from '@adornis/filter/aspect-schemas/StringAspect.js';
import '@adornis/filter/client/x-filter-editor.js';
import '@adornis/popover/x-dropdown-selection';
import { goTo } from '@adornis/router/client/open-href.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { map } from 'rxjs';
import { buildBuddyExportBuildOptions } from '../api/BuddyExportAPI.js';
import { BUDDY_EXPORT_ENUM_FIELDS, BUDDY_EXPORT_ENUM_LIST_FIELDS } from '../db/BuddyExportDataView.js';
import { BuddyDialogPrepareExport } from './buddy-dialog-prepare-export.js';

@customElement('buddy-page-export')
export class BuddyPageExport extends ChemistryLitElement {
  private _defaultView = new View({
    configuredColumns: [
      {
        fieldName: 'email',
      },
      {
        fieldName: '_class',
      },
    ],
  });

  private _localStorage = new LocalStorageReactive<View<typeof BuddyUser>>('ExportView', this._defaultView);
  @state() private _curView = new RXController(
    this,
    this._localStorage.pipe(
      map(curView => (curView ? constructValue<View<typeof BuddyUser>>(curView) : this._defaultView)),
    ),
  );

  translator = new TranslationController<TranslationDictionaryBuddy>(this);
  currentTimeframe = new RXController(this, currentlySelectedTimeframe);

  protected actionRow(dataOptions: DataViewOptions<typeof BuddyUser>): Renderable[] {
    return [
      html`
        <tr-button
          icon="download"
          @click=${() => {
            const timeframe = this.currentTimeframe.value;

            BuddyDialogPrepareExport.showPopup({
              props: {
                dataOptions,
                timeframe,
                userClass: getMenteeUserClass(),
              },
            });
          }}
        >
          ${this.translator.translate('buddy', 'exportMenteeUser')}
        </tr-button>
        <tr-button
          icon="download"
          @click=${() => {
            const timeframe = this.currentTimeframe.value;

            BuddyDialogPrepareExport.showPopup({
              props: {
                dataOptions,
                timeframe,
                userClass: getMentorUserClass(),
              },
            });
          }}
        >
          ${this.translator.translate('buddy', 'exportMentorUser')}
        </tr-button>
      `,
      html` <slot name="additional-buttons"></slot> `,
    ];
  }

  override render() {
    const currentTimeframe = this.currentTimeframe;
    if (currentTimeframe.isLoading) return LOADER;
    if (!currentTimeframe.value)
      return html` <x-text> ${this.translator.translate('buddy', 'noTimeFrameChoosen')} </x-text> `;

    const dataOptions: DataViewOptions<typeof BuddyUser> = {
      class: BuddyUser,
      view: this._curView.value ?? this._defaultView,
      customColumns: [
        { fieldName: 'email' },
        {
          fieldName: '_class',
          filterAspect: {
            aspectConstructor: StringAspectConfig('equals').aspectConstructor,
            renderEditor: () => html`
              <x-dropdown-selection
                .selectables=${[BuddyUserMentee._class, BuddyUserMentor._class]}
              ></x-dropdown-selection>
            `,
          },
        },
        ...BUDDY_EXPORT_ENUM_LIST_FIELDS.map(fieldName => ({
          fieldName,
          renderItem: userEntity => {
            return userEntity[fieldName]
              ? html`
                  <x-flex space="xxs">
                    ${(userEntity[fieldName] ?? []).map(
                      curEnum => html` <x-text>${this.translator.translate('enums', curEnum)},</x-text> `,
                    )}
                  </x-flex>
                `
              : nothing;
          },
        })),
        ...BUDDY_EXPORT_ENUM_FIELDS.map(fieldName => ({
          fieldName,
          renderItem: userEntity =>
            userEntity[fieldName]
              ? html` <x-text>${this.translator.translate('enums', userEntity[fieldName])}</x-text> `
              : nothing,
        })),
      ],
      onViewChanged: async nextView => this._localStorage.next(nextView),
    };

    const opts = buildBuddyExportBuildOptions(dataOptions, currentTimeframe.value);

    return html`
      <tr-flex space="lg">
        <bh-breadcrumb .goUp=${() => goTo('/')}></bh-breadcrumb>

        <tr-flex space="md">
          <tr-h2> ${this.translator.translate('route', 'export')} </tr-h2>
          <tr-flex space="md">
            <tr-flex horizontal space="sm"> ${this.actionRow(dataOptions)} </tr-flex>

            <tr-filter-editor .options=${opts.filterOptions}></tr-filter-editor>
            <tr-data-view .options=${opts.dataViewOptions}></tr-data-view>
          </tr-flex>
        </tr-flex>
      </tr-flex>
    `;
  }
}
